/**
 * The recording's level, as bars: the phone's `LevelBars` in
 * `agent-input-row.tsx`.
 *
 * **A meter, not a loop.** Each bar is the one level scaled by its own share,
 * so it stands still in a silent room and moves only with the voice — unlike
 * `VoiceWave`, which pulses to say the mic is open. The bars follow the level
 * themselves, by writing their own transform through a ref, so a level written
 * forty times a second re-renders nothing. A transform, never a height, so the
 * compositor does the work.
 */
import { useEffect, useRef, type ReactElement } from 'react';

import { cn } from '~/lib/utils';

import type { VoiceLevel } from '../level';

const BAR_SHARES = [0.45, 0.75, 1, 0.8, 0.6, 0.9, 0.5] as const;
/**
 * The wide meter the OS composer draws while it dictates (parts.mjs
 * `levelWave`): the board's own heights, as shares of the tallest, run across
 * the row and cut where the row ends.
 */
const WIDE_SHARES: readonly number[] = [
  4, 4, 8, 12, 8, 16, 20, 12, 20, 16, 8, 12, 20, 20, 16, 12, 8, 16, 20, 12, 8, 4, 8, 12, 16, 12, 8, 4, 4, 8, 12, 20, 16, 12, 8, 12, 16, 8, 4, 4,
  8, 16, 20, 20, 12, 8, 4, 4, 8, 12, 8, 16, 12, 8, 4, 4, 8, 12, 16, 8, 4, 4, 8, 12, 8, 4, 4, 8, 12, 16, 20, 12, 8, 4, 4, 8, 12, 8, 4, 4, 4, 8, 12, 8,
].map((height) => height / 20);
/** The height a bar keeps in silence, as a share of its full height: a row of dots, not nothing. */
const BAR_REST = 0.17;

const scaleOf = (level: number, share: number): string => {
  const heard = Math.max(0, Math.min(1, level));
  return `scaleY(${BAR_REST + (1 - BAR_REST) * heard * share})`;
};

export type LevelBarsProps = {
  readonly level: VoiceLevel;
  /** `wide`: the composer's meter, thin bars across the whole row. */
  readonly variant?: 'short' | 'wide';
  readonly className?: string;
};

export function LevelBars({ level, variant = 'short', className }: LevelBarsProps): ReactElement {
  const bars = useRef<(HTMLSpanElement | null)[]>([]);
  const shares = variant === 'wide' ? WIDE_SHARES : BAR_SHARES;

  useEffect(() => {
    const paint = (value: number): void => {
      shares.forEach((share, at) => {
        const bar = bars.current[at];
        if (bar !== null && bar !== undefined) bar.style.transform = scaleOf(value, share);
      });
    };
    paint(level.value);
    return level.subscribe(paint);
  }, [level, shares]);

  const wide = variant === 'wide';
  return (
    <span
      className={cn('flex items-center', wide ? 'h-5 min-w-0 flex-1 gap-0.5 overflow-hidden' : 'h-6 gap-1', className)}
      aria-hidden
      data-slot="level-bars"
      data-variant={variant}
    >
      {shares.map((share, at) => (
        <span
          key={at}
          ref={(node) => {
            bars.current[at] = node;
          }}
          className={wide ? 'h-5 w-0.5 shrink-0 rounded-max bg-foreground-subtle' : 'h-6 w-1 rounded-max bg-foreground'}
          style={{ transform: scaleOf(0, share) }}
        />
      ))}
    </span>
  );
}
